import React, { useState } from "react";
import { ScrollText, Loader2, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export const CREED_VERSION = "1.0";

export const CREED_CLAUSES = [
  { n: "1", h: "You are responsible for yourself.", b: "Sovereign Signal is a private, invite-only, member-driven space. Every member is a sovereign adult, fully responsible for their own words, actions, decisions, and outcomes, both inside this space and in any interaction, exchange, meeting, or relationship that begins here." },
  { n: "2", h: "Not professional advice.", b: "Nothing shared on Sovereign Signal (by members, stewards, or the founder) is legal, financial, medical, tax, investment, or professional advice. Content is shared for informational and community purposes only. You are solely responsible for how you use it, and you should seek qualified professionals before acting." },
  { n: "3", h: "Assumption of risk.", b: "You understand that member-submitted content, businesses, trades, systems, resources, and meet-ups are not vetted, endorsed, or guaranteed by Sovereign Signal or its founder. You participate, connect, transact, and meet entirely at your own risk." },
  { n: "4", h: "Release of liability.", b: "To the fullest extent permitted by law, you release, waive, and agree not to pursue any claim, demand, or legal action against the founder, stewards, or operators of Sovereign Signal for any loss, harm, damage, dispute, or injury arising from your use of this space or your interactions with other members. You accept full personal responsibility." },
  { n: "5", h: "Your content, your responsibility.", b: "Sovereign Signal is end-to-end encrypted, the operators cannot read, monitor, or moderate private content. You are solely responsible for anything you post, share, submit, or transmit, and you agree not to share anything unlawful or that violates the rights of others." },
  { n: "6", h: "Conduct & exchange between members.", b: "Any agreement, transaction, collaboration, or exchange between members is strictly between those members. Sovereign Signal is not a party to it and bears no responsibility for its outcome." },
  { n: "7", h: "Privacy & encryption.", b: "You understand that end-to-end encryption means only you hold the keys to your content. If you lose your password and recovery phrase, your content cannot be recovered by anyone, including the operators." },
  { n: "8", h: "\"As is.\"", b: "Sovereign Signal is provided \"as is,\" without warranties of any kind. Access may change, pause, or end at any time." },
  { n: "9", h: "You confirm you are of legal age (18+)", b: "and are entering freely and voluntarily." },
];

const CreedBody = () => (
  <div className="space-y-4" data-testid="creed-text">
    {CREED_CLAUSES.map((c) => (
      <div key={c.n} className="flex gap-3">
        <span className="font-heading text-sm text-[#14A89A] shrink-0 w-4">{c.n}</span>
        <p className="font-body text-sm text-zinc-300 leading-relaxed">
          <span className="text-white font-medium">{c.h}</span> {c.b}
        </p>
      </div>
    ))}
  </div>
);

export const SovereignCreed = () => {
  const { user, setUser } = useAuth();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const needed = user && (user.role === "admin" || user.status === "approved") && user.creed_version !== (user.active_creed_version || CREED_VERSION);
  if (!needed) return null;

  const accept = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/creed/accept");
      setUser({ ...user, creed_accepted_at: data.creed_accepted_at, creed_version: data.creed_version });
    } catch {
      setError("Could not record your agreement. Check your connection and try again.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-[#0A0A0A] flex items-center justify-center p-4" data-testid="sovereign-creed-modal">
      <div className="bg-[#121212] border border-[#14A89A]/30 w-full max-w-lg max-h-[92vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2 text-[#D4AF37] mb-1"><ScrollText size={18} /><span className="font-body text-[10px] tracking-[0.3em] uppercase">Member Agreement</span></div>
          <h2 className="font-heading text-2xl text-white">The Sovereign Creed</h2>
          <p className="font-body text-xs text-zinc-500 mt-2">Please read this once before you enter. You'll only see it this one time.</p>
        </div>

        <div className="px-6 py-5 overflow-y-auto">
          <CreedBody />
        </div>

        <div className="px-6 py-5 border-t border-white/10 shrink-0 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="creed-checkbox-label">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid="creed-checkbox" className="accent-[#14A89A] w-5 h-5 mt-0.5 shrink-0" />
            <span className="font-body text-sm text-zinc-200 leading-snug">I have read and agree. I take full responsibility for myself.</span>
          </label>
          {error && <p className="font-body text-sm text-red-400" data-testid="creed-error">{error}</p>}
          <button onClick={accept} disabled={!checked || busy} data-testid="creed-agree-button" className="w-full bg-[#14A89A] text-[#0A0A0A] font-medium py-3.5 font-body text-sm hover:bg-[#2BC4B5] transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} Agree &amp; Enter
          </button>
        </div>
      </div>
    </div>
  );
};

// Read-only viewer so members can re-read the Creed anytime.
export const CreedViewer = ({ onClose, acceptedAt, version }) => (
  <div className="fixed inset-0 z-[300] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="creed-viewer-modal" onClick={onClose}>
    <div className="bg-[#121212] border border-[#14A89A]/30 w-full max-w-lg max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
      <div className="px-6 pt-6 pb-4 border-b border-white/10 shrink-0 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#D4AF37] mb-1"><ScrollText size={18} /><span className="font-body text-[10px] tracking-[0.3em] uppercase">Member Agreement</span></div>
          <h2 className="font-heading text-2xl text-white">The Sovereign Creed</h2>
          {acceptedAt && <p className="font-body text-[11px] text-zinc-500 mt-2">You accepted v{version || "1.0"} on {new Date(acceptedAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}.</p>}
        </div>
        <button onClick={onClose} data-testid="creed-viewer-close" className="text-zinc-500 hover:text-white shrink-0"><X size={20} /></button>
      </div>
      <div className="px-6 py-5 overflow-y-auto"><CreedBody /></div>
    </div>
  </div>
);

